import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ESkeletonType } from '@norfolk-southern/accessns-components';
import { CalendarEvent, CalendarMonthViewBeforeRenderEvent, CalendarMonthViewDay } from 'angular-calendar';
import { Subject } from 'rxjs';

import { PDFPrintGenerator } from '@ruby/configs/common.constants';
import { IUTEventVessel } from '@ruby/modules/unit-train/models/unit-train-permit.interface';
import { VesselEventContainer } from '@ruby/shared/dialogs/unit-train/vessel-event/containers/vessel-event.container';

@Component({
  selector: 'ng-calendar-vessel',
  templateUrl: './calendar-vessel.component.html',
  styleUrls: ['./calendar-vessel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarVesselComponent implements OnChanges, OnDestroy {
  @Input() events: Array<IUTEventVessel> = [];
  @Input() loading!: boolean;
  @Input() currentDate: Date = new Date();
  @Output() makeEventsSearch = new EventEmitter<{ month: number; year: number }>();
  @ViewChild('calendarContainer') calendarContainer!: ElementRef;
  allTypesSelected = true;
  calendarEvents: Array<CalendarEvent> = [];
  skeleton: ESkeletonType = ESkeletonType.WIDGET;
  private subscriptionNotifier = new Subject<void>();

  constructor(private matDialog: MatDialog, private cdr: ChangeDetectorRef) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.events?.currentValue) {
      this.setupCalendarEvents(changes.events.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.subscriptionNotifier.next();
    this.subscriptionNotifier.complete();
  }

  /**
   * Block Days
   *
   * @summary Blocks any given day in the calendar
   * @param renderEvent: CalendarMonthViewBeforeRenderEvent
   * @returns void
   */
  blockDays(renderEvent: CalendarMonthViewBeforeRenderEvent): void {
    renderEvent.body.forEach((day: CalendarMonthViewDay) => {
      if ((day.isPast || day.isFuture) && !day.inMonth) {
        day.cssClass = 'cal-cell-day-muted';
      }
    });
  }

  /**
   * Handle Event Click
   *
   * @summary Handle event click to display permit details
   * @param $event: CalendarEvent
   * @returns void
   */
  handleEventClick($event: CalendarEvent): void {
    this.matDialog.open(VesselEventContainer, {
      data: { event: $event.meta.event },
      width: '60vw',
      height: '80vh',
      autoFocus: false
    });
  }

  /**
   * Get Update Events
   *
   * @summary Get Update Events
   * @returns void
   */
  getUpdateEvents(): void {
    this.makeEventsSearch.emit({ month: this.currentDate.getMonth(), year: this.currentDate.getFullYear() });
  }

  /**
   * Print Calendar
   *
   * @summary Prints the displaying calendar
   * @returns void
   */
  printCalendar(): void {
    PDFPrintGenerator.print('Vessel Calendar', this.calendarContainer.nativeElement.innerHTML, 'ut-calendar');
  }

  private setupCalendarEvents(events: Array<IUTEventVessel>): void {
    this.calendarEvents = events.map((item: IUTEventVessel) => ({
      title: item.vesselName,
      start: new Date(item.start),
      end: new Date(item.end),
      meta: {
        status: item.vesselEventStatus,
        tonnage: item.tonnageNominated,
        finalCustomer: item.vesselEventFinalCustomer,
        nominees: item.nominees,
        event: item
      }
    }));
  }
}
